import { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) return <Navigate to="/app" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-nx-dark bg-grid flex items-center justify-center px-6 font-jakarta" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="block text-center mb-8" data-testid="login-logo">
          <img src="/logo-mark.png" alt="NX" className="h-16 w-auto mx-auto" />
          <span className="block font-mono text-xs tracking-[0.45em] text-white mt-2">COMPANY</span>
          <span className="block text-[10px] font-mono font-normal text-zinc-500 tracking-[0.4em] mt-1">PLATAFORMA DE GESTÃO</span>
        </Link>
        <div className="glass rounded-3xl p-8" data-testid="login-card">
          <h1 className="font-syne text-2xl font-bold text-white" data-testid="login-title">
            Acessar plataforma
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            ERP & CRM integrado da NX COMPANY
          </p>
          <form onSubmit={submit} className="mt-7 space-y-5">
            <div>
              <label htmlFor="email" className="text-xs font-mono uppercase tracking-widest text-zinc-400">E-mail</label>
              <input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-2 w-full bg-zinc-900/80 border border-zinc-700 focus:border-nx-yellow focus:outline-none rounded-xl px-4 py-3 text-sm text-white transition-colors"
                placeholder="voce@empresa.com.br"
              />
            </div>
            <div>
              <label htmlFor="password" className="text-xs font-mono uppercase tracking-widest text-zinc-400">Senha</label>
              <div className="relative mt-2">
                <input
                  id="password"
                  type={showPw ? "text" : "password"}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  className="w-full bg-zinc-900/80 border border-zinc-700 focus:border-nx-yellow focus:outline-none rounded-xl px-4 py-3 pr-11 text-sm text-white transition-colors"
                  placeholder="••••••••"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} data-testid="toggle-password-visibility" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-nx-yellow">
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            {error && (
              <p className="text-sm text-red-400 bg-red-400/10 border border-red-400/30 rounded-xl px-4 py-2.5" data-testid="login-error">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-nx-yellow text-nx-dark font-bold py-3.5 rounded-xl hover:bg-nx-electric transition-colors disabled:opacity-50"
            >
              {loading ? "Aguarde..." : "Entrar"}
            </button>
          </form>
          <div className="mt-5 text-xs">
            <Link to="/forgot-password" className="text-zinc-400 hover:text-nx-yellow transition-colors" data-testid="link-to-forgot">
              Esqueci minha senha
            </Link>
          </div>
        </div>
        <Link to="/" className="block text-center text-xs text-zinc-500 hover:text-zinc-300 mt-6 transition-colors" data-testid="back-to-site">
          ← Voltar para o site
        </Link>
      </div>
    </div>
  );
}
